import { useIntl } from 'react-intl';
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  FormInstance,
  Checkbox,
  Typography,
  Modal,
  Space,
} from 'antd';

import { useState, useEffect } from 'react';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import { usePlanAccount } from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.hooks';

import {
  Group,
  InputDate,
  InputDecimal,
  PlanAccountsSelect,
} from '@/components/_common';
import { useDefaultEmptyFieldError } from '@/hooks';
import {
  BankingEntry,
  BankingEntryFormValues,
  Destination,
} from '@/core/domain/financial/BankingEntry/BankingEntry.types';
import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import moment from 'moment';
import CompaniesInfiniteSelectField from '@/components/_common/CompaniesInfiniteSelectField';
import BankAccountInfiniteSelectField from '@/components/_common/BankAccountInfiniteSelectField';
import DocumentTypesInfiniteSelectField from '@/components/_common/DocumentTypesInfiniteSelectField';
import { useDocumentTypeMutation } from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.hooks';
import { HelpCircle } from 'react-feather';

interface Props {
  form: FormInstance<BankingEntryFormValues>;
  isEdit?: boolean;
  bankingEntry?: BankingEntry;
}

function BankingEntryForm({ form, isEdit, bankingEntry }: Props) {
  const intl = useIntl();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const [planAccount, setPlanAccount] = useState<number>();

  const { data: planAccountSelected, mutateAsync: planAccountMutation } =
    usePlanAccount();

  const documentTypeDetail = useDocumentTypeMutation();

  const isGenerateAccount =
    !!bankingEntry?.installmentPayableId ||
    !!bankingEntry?.installmentReceiveId;

  const disabled = bankingEntry?.reversed || isGenerateAccount;

  useEffect(() => {
    if (planAccount) {
      planAccountMutation(planAccount);
    }
  }, [planAccount]);

  useEffect(() => {
    if (planAccountSelected) {
      if (planAccountSelected?.destination === PlanAccountDestination.Expense) {
        form.setFieldsValue({
          nature: Destination.expense,
        });
      } else {
        form.setFieldsValue({
          nature: Destination.revenue,
        });
      }
    }
  }, [planAccountSelected]);

  const handleFormValuesChange = (valuesChanged: BankingEntryFormValues) => {
    handlePlanAccountsChange(valuesChanged);

    if (valuesChanged.transfer) {
      form.resetFields(['bankAccountIdTransfer', 'planAccountIdTransfer']);
    }

    if (valuesChanged.planAccountId) {
      form.resetFields(['planAccountIdTransfer']);
    }
  };

  const handlePlanAccountsChange = (value: BankingEntryFormValues) => {
    setPlanAccount(value.planAccountId);
  };

  const handleDocumentTypeChange = async (documentTypeId: number) => {
    const documentType = await documentTypeDetail.mutateAsync(documentTypeId);

    if (documentType && documentType.payOnTime) {
      form.setFieldValue('bankAccountId', documentType.bankAccountId);
    }
  };

  const amountAdjusted = Form.useWatch('amountAdjusted', form);

  return (
    <Form
      layout="vertical"
      form={form}
      onValuesChange={handleFormValuesChange}
      name="bankingEntryForm"
    >
      <Row gutter={[16, 9]}>
        {typeof bankingEntry?.code === 'number' && (
          <Col span={24}>
            <Typography.Title level={5}>{`Lançamento bancário código: ${
              !bankingEntry?.code ? '' : bankingEntry?.code
            }`}</Typography.Title>
          </Col>
        )}

        <Col span={24} md={8} lg={8} xxl={8}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'releaseBankEntry.form.company',
            })}
            name="companyId"
            rules={[defaultEmptyFieldError]}
          >
            <CompaniesInfiniteSelectField
              queryFilter={{ active: true }}
              disabled={disabled}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8} lg={8} xxl={8}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'releaseBankEntry.form.description',
            })}
            name="description"
            rules={!isGenerateAccount ? [defaultEmptyFieldError] : []}
          >
            <Input disabled={disabled} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8} lg={8} xxl={8}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'releaseBankEntry.form.documentNumber',
            })}
            name="documentNumber"
          >
            <Input disabled={disabled} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8} lg={8} xxl={8}>
          <FloatingFormItem
            name="bankAccountId"
            label={intl.formatMessage({
              id: 'releaseBankEntry.form.bankAccount',
            })}
            rules={[defaultEmptyFieldError]}
          >
            <BankAccountInfiniteSelectField
              queryFilter={{ order: 'DESC', active: true }}
              disabled={disabled || !!documentTypeDetail.data?.bankAccountId}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8} lg={8} xxl={8}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'releaseBankEntry.form.documentType',
            })}
            name="documentTypeId"
            rules={[defaultEmptyFieldError]}
          >
            <DocumentTypesInfiniteSelectField
              onChange={handleDocumentTypeChange}
              queryFilter={{ active: true }}
              disabled={disabled}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8} lg={8} xxl={8}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'releaseBankEntry.form.planAccount',
            })}
            name="planAccountId"
            rules={[defaultEmptyFieldError]}
          >
            <PlanAccountsSelect
              queryFilter={{
                hierarchy: PlanAccountHierarchy.Analitical,
                active: true,
              }}
              disabled={disabled}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8} lg={8} xxl={4}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'releaseBankEntry.form.destination',
            })}
            name="nature"
          >
            <Select style={{ width: '100%' }} disabled>
              <Select.Option value={Destination.expense}>
                {intl.formatMessage({
                  id: 'releaseBankEntry.domain.enums.nature_type.expense',
                })}
              </Select.Option>

              <Select.Option value={Destination.revenue}>
                {intl.formatMessage({
                  id: 'releaseBankEntry.domain.enums.nature_type.revenue',
                })}
              </Select.Option>
            </Select>
          </FloatingFormItem>
        </Col>

        {!!bankingEntry?.bankReleaseCode && (
          <Col span={24} md={8} lg={8} xxl={3}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'releaseBankEntry.form.LaunchCode',
              })}
              name="bankReleaseCode"
            >
              <Input disabled />
            </FloatingFormItem>
          </Col>
        )}

        {isGenerateAccount && (
          <Col span={24} md={8} lg={8} xxl={3}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'releaseBankEntry.form.installmentCode',
              })}
              name="installmentAccountCode"
            >
              <Input disabled />
            </FloatingFormItem>
          </Col>
        )}

        <Col span={24} md={8} lg={8} xxl={4}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'releaseBankEntry.form.releaseDate',
            })}
            name="launchDate"
            initialValue={moment()}
          >
            <InputDate
              disabledDate={(current) => {
                return moment().add('days') <= current;
              }}
              disabled={disabled}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8} lg={8} xxl={4}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'releaseBankEntry.form.conciliationDate',
            })}
            name="conciliationDate"
          >
            <InputDate
              disabledDate={(current) => {
                return moment().add('days') <= current;
              }}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8} lg={8} xxl={6}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'releaseBankEntry.form.value',
            })}
            name="value"
            rules={[defaultEmptyFieldError]}
          >
            <InputDecimal disabled={disabled} min={0.01} />
          </FloatingFormItem>
        </Col>

        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const rateValue = getFieldValue('rateValue');

            if (typeof rateValue === 'number') {
              return (
                <Col span={24} md={8} lg={8} xxl={5}>
                  <FloatingFormItem label="(R$) Tarifa" name="rateValue">
                    <InputDecimal disabled />
                  </FloatingFormItem>
                </Col>
              );
            }
          }}
        </Form.Item>

        {isEdit && !!bankingEntry?.authorizationNumber && (
          <Col span={24} md={8} lg={8} xxl={4}>
            <FloatingFormItem
              label="Número de autorização"
              name="authorizationNumber"
            >
              <Input disabled />
            </FloatingFormItem>
          </Col>
        )}

        {isEdit && !!amountAdjusted && (
          <Col span={24} md={12} lg={6}>
            <Group style={{ width: '100%' }} gap={10}>
              <FloatingFormItem
                label="Valor ajustado"
                name="amountAdjusted"
                initialValue={0}
              >
                <InputDecimal disabled />
              </FloatingFormItem>

              <HelpCircle
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  Modal.info({
                    title: (
                      <Space direction="vertical">
                        <Typography.Text>
                          Campo referente a valores que o cliente pagou de
                          diferença no boleto: Juros, Multa, Descontos, etc.
                        </Typography.Text>
                      </Space>
                    ),
                  })
                }
              />
            </Group>
          </Col>
        )}

        {!isEdit && (
          <>
            {!isGenerateAccount && !bankingEntry?.bankReleaseCode && (
              <>
                <Col span={24}>
                  <Form.Item
                    name="transfer"
                    initialValue={false}
                    valuePropName="checked"
                  >
                    <Checkbox style={{ marginTop: '12px' }}>
                      {intl.formatMessage({
                        id: 'releaseBankEntry.form.transfer',
                      })}
                    </Checkbox>
                  </Form.Item>
                </Col>
              </>
            )}

            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) => {
                const transfer = getFieldValue('transfer');

                if (transfer) {
                  return (
                    <>
                      <Col span={24} md={8} lg={8} xxl={8}>
                        <FloatingFormItem
                          name="bankAccountIdTransfer"
                          label={intl.formatMessage({
                            id: 'releaseBankEntry.form.bankAccount',
                          })}
                        >
                          <BankAccountInfiniteSelectField
                            queryFilter={{ order: 'DESC', active: true }}
                          />
                        </FloatingFormItem>
                      </Col>

                      <Col span={24} md={8} lg={8} xxl={8}>
                        <FloatingFormItem
                          label={intl.formatMessage({
                            id: 'releaseBankEntry.form.planAccount',
                          })}
                          name="planAccountIdTransfer"
                          rules={[defaultEmptyFieldError]}
                        >
                          <PlanAccountsSelect
                            queryFilter={{
                              active: true,
                              destination:
                                planAccountSelected?.destination ===
                                PlanAccountDestination.Expense
                                  ? PlanAccountDestination.Revenue
                                  : PlanAccountDestination.Expense,
                              hierarchy: PlanAccountHierarchy.Analitical,
                            }}
                          />
                        </FloatingFormItem>
                      </Col>

                      <Col span={24} md={8} lg={8} xxl={8}>
                        <FloatingFormItem
                          name="companyIdTransfer"
                          label="Empresa"
                        >
                          <CompaniesInfiniteSelectField
                            allowClear
                            queryFilter={{ active: true }}
                          />
                        </FloatingFormItem>
                      </Col>

                      <Col span={24} md={8} lg={8} xxl={8}>
                        <FloatingFormItem
                          name="documentTypeIdTransfer"
                          label="Tipo de documento"
                        >
                          <DocumentTypesInfiniteSelectField
                            allowClear
                            queryFilter={{ active: true }}
                          />
                        </FloatingFormItem>
                      </Col>
                    </>
                  );
                }
              }}
            </Form.Item>
          </>
        )}
      </Row>
    </Form>
  );
}

export default BankingEntryForm;
